import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  AvatarGroup,
  Button,
  IconButton,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Chip,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fade,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Users, Plus, MoreVertical, Trash2, Edit2, ChevronDown, ChevronUp, UserPlus, X, FolderPlus, Folder, Copy } from 'lucide-react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { usePermissions } from '../../hooks/usePermissions';
import { showToast } from '../../store/uiSlice';
import { fetchWorkspace, fetchWorkspaces, fetchWorkspaceBoards } from '../../store/workspaceSlice';
import {
  fetchWorkspaceTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  addTeamMember,
  removeTeamMember,
} from '../../store/slices/teamsSlice';
import { Team, TeamMemberRole } from '../../api/types/TeamTypes';
import { teamsApi } from '../../api/endpoints/teamsApi';
import { boardsApi } from '../../api/endpoints/boardsApi';
import './TeamsPage.scss';

interface AssignedBoard {
  id: number;
  name: string;
  key: string | null;
}

const TeamsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { id: routeWorkspaceId } = useParams<{ id: string }>();
  const { currentWorkspace, workspaces } = useAppSelector((state) => state.workspaces);
  const workspaceId = routeWorkspaceId || currentWorkspace?.id;

  const { teamsByWorkspace, loading } = useAppSelector((state) => state.teams);
  const teams = (workspaceId && teamsByWorkspace[workspaceId]) || [];
  const { workspaceBoards } = useAppSelector((state) => state.workspaces);

  const { canManageMembers } = usePermissions(workspaceId);

  const [teamBoards, setTeamBoards] = useState<Record<number, AssignedBoard[]>>({});
  const [loadingBoardsForTeam, setLoadingBoardsForTeam] = useState<number | null>(null);
  const [assignBoardTeamId, setAssignBoardTeamId] = useState<number | null>(null);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [assigningBoard, setAssigningBoard] = useState(false);

  const [expandedTeamId, setExpandedTeamId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [creating, setCreating] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  const [renamingTeam, setRenamingTeam] = useState<Team | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingTeam, setDeletingTeam] = useState<Team | null>(null);
  const [menuTeam, setMenuTeam] = useState<Team | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const [copyingTeam, setCopyingTeam] = useState<Team | null>(null);
  const [copyTargetWorkspaceId, setCopyTargetWorkspaceId] = useState('');
  const [copying, setCopying] = useState(false);

  const [addMemberTeamId, setAddMemberTeamId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTeams = searchQuery.trim()
    ? teams.filter((t) => t.name.toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : teams;

  useEffect(() => {
    if (workspaceId) {
      dispatch(fetchWorkspaceTeams(workspaceId));
    }
  }, [dispatch, workspaceId]);

  // Team member picker needs the full workspace roster - fetch workspace
  // detail if it isn't already loaded (matches Team.tsx's own pattern).
  useEffect(() => {
    if (workspaceId && (!currentWorkspace || currentWorkspace.id !== workspaceId || !currentWorkspace.members)) {
      dispatch(fetchWorkspace(workspaceId));
    }
  }, [dispatch, workspaceId, currentWorkspace]);

  // Board-assignment picker needs the workspace's board list.
  useEffect(() => {
    if (workspaceId) {
      dispatch(fetchWorkspaceBoards(workspaceId));
    }
  }, [dispatch, workspaceId]);

  // The "Copy to workspace" target picker needs the full workspace list.
  useEffect(() => {
    if (workspaces.length === 0) {
      dispatch(fetchWorkspaces());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!workspaceId) {
    return (
      <Box className="teams-page">
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Users size={48} strokeWidth={1} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            No workspace selected
          </Typography>
        </Box>
      </Box>
    );
  }

  const handleCreate = async () => {
    if (!newTeamName.trim()) return;
    setCreating(true);
    try {
      await dispatch(createTeam({ workspaceId, data: { name: newTeamName.trim() } })).unwrap();
      dispatch(showToast({ message: `Team "${newTeamName.trim()}" created`, type: 'success' }));
      setNewTeamName('');
      setCreateOpen(false);
    } catch (error: any) {
      const status = error?.response?.status ?? error?.status;
      if (status === 409) {
        setDuplicateOpen(true);
      } else {
        dispatch(
          showToast({
            message: error?.response?.data?.message || error?.message || 'Failed to create team',
            type: 'error',
          }),
        );
      }
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async () => {
    if (!renamingTeam || !renameValue.trim()) return;
    try {
      await dispatch(
        updateTeam({ workspaceId, teamId: renamingTeam.id, data: { name: renameValue.trim() } }),
      ).unwrap();
      dispatch(showToast({ message: 'Team renamed', type: 'success' }));
      setRenamingTeam(null);
    } catch (error: any) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || error?.message || 'Failed to rename team',
          type: 'error',
        }),
      );
    }
  };

  const handleDelete = async () => {
    if (!deletingTeam) return;
    try {
      await dispatch(deleteTeam({ workspaceId, teamId: deletingTeam.id })).unwrap();
      dispatch(showToast({ message: `Team "${deletingTeam.name}" deleted`, type: 'success' }));
      setDeletingTeam(null);
    } catch (error: any) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || error?.message || 'Failed to delete team',
          type: 'error',
        }),
      );
    }
  };

  const handleAddMember = async () => {
    if (addMemberTeamId === null || !selectedUserId) return;
    try {
      await dispatch(
        addTeamMember({
          workspaceId,
          teamId: addMemberTeamId,
          data: { userId: Number(selectedUserId) },
        }),
      ).unwrap();
      dispatch(showToast({ message: 'Member added to team', type: 'success' }));
      setSelectedUserId('');
    } catch (error: any) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || error?.message || 'Failed to add member',
          type: 'error',
        }),
      );
    }
  };

  const handleRemoveMember = async (teamId: number, userId: number) => {
    try {
      await dispatch(removeTeamMember({ workspaceId, teamId, userId })).unwrap();
      dispatch(showToast({ message: 'Member removed from team', type: 'success' }));
    } catch (error: any) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || error?.message || 'Failed to remove member',
          type: 'error',
        }),
      );
    }
  };

  const loadTeamBoards = async (teamId: number) => {
    setLoadingBoardsForTeam(teamId);
    try {
      const boards = await teamsApi.getAssignedBoards(teamId);
      setTeamBoards((prev) => ({ ...prev, [teamId]: boards }));
    } catch (error) {
      console.error('Failed to load assigned projects:', error);
    } finally {
      setLoadingBoardsForTeam(null);
    }
  };

  const handleToggleExpand = (teamId: number) => {
    const next = expandedTeamId === teamId ? null : teamId;
    setExpandedTeamId(next);
    if (next !== null && !teamBoards[next]) {
      loadTeamBoards(next);
    }
  };

  const handleAssignBoard = async (teamId: number) => {
    if (!selectedBoardId) return;
    setAssigningBoard(true);
    try {
      await boardsApi.assignTeam(selectedBoardId, teamId);
      dispatch(showToast({ message: 'Team assigned to project', type: 'success' }));
      setSelectedBoardId('');
      loadTeamBoards(teamId);
    } catch (error: any) {
      const status = error?.response?.status;
      dispatch(
        showToast({
          message:
            status === 409
              ? 'This team is already assigned to that project'
              : error?.response?.data?.message || error?.message || 'Failed to assign project',
          type: 'error',
        }),
      );
    } finally {
      setAssigningBoard(false);
    }
  };

  const handleUnassignBoard = async (teamId: number, boardId: number) => {
    try {
      await boardsApi.unassignTeam(String(boardId), teamId);
      dispatch(showToast({ message: 'Team removed from project', type: 'success' }));
      loadTeamBoards(teamId);
    } catch (error: any) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || error?.message || 'Failed to remove project',
          type: 'error',
        }),
      );
    }
  };

  const handleCopyToWorkspace = async () => {
    if (!copyingTeam || !copyTargetWorkspaceId) return;
    setCopying(true);
    try {
      const copied = await teamsApi.copyToWorkspace(copyingTeam.id, copyTargetWorkspaceId);
      const targetName = workspaces.find((w) => w.id === copyTargetWorkspaceId)?.name || 'the workspace';
      dispatch(
        showToast({
          message:
            copied.name === copyingTeam.name
              ? `Copied "${copyingTeam.name}" to ${targetName}`
              : `Copied "${copyingTeam.name}" to ${targetName} as "${copied.name}" (name already taken there)`,
          type: 'success',
        }),
      );
      setCopyingTeam(null);
      setCopyTargetWorkspaceId('');
    } catch (error: any) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || error?.message || 'Failed to copy team',
          type: 'error',
        }),
      );
    } finally {
      setCopying(false);
    }
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, team: Team) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuTeam(team);
  };
  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuTeam(null);
  };

  const workspaceMembers = (currentWorkspace?.members || []).filter(Boolean);

  return (
    <Box className="teams-page">
      <Box className="teams-page__header">
        <Box>
          <Typography variant="h4" className="teams-page__title">
            Teams
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Group members together and assign them to projects as a unit
          </Typography>
        </Box>
        {canManageMembers && (
          <Button variant="contained" startIcon={<Plus size={20} />} onClick={() => setCreateOpen(true)}>
            Create Team
          </Button>
        )}
      </Box>

      {teams.length > 0 && (
        <TextField
          size="small"
          placeholder="Search teams..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2, width: 280 }}
        />
      )}

      {loading && teams.length === 0 ? (
        <Box className="teams-page__list">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="team-card">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Skeleton variant="text" width="35%" height={28} />
                  <Skeleton variant="rounded" width={90} height={22} sx={{ borderRadius: 999 }} />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : teams.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Users size={48} strokeWidth={1} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            No teams yet
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {canManageMembers
              ? 'Create a team to group members and assign them to projects together'
              : 'No teams have been created in this workspace yet'}
          </Typography>
        </Box>
      ) : filteredTeams.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No teams match "{searchQuery}"
        </Typography>
      ) : (
        <Box className="teams-page__list">
          {filteredTeams.map((team, i) => {
            const members = team.members || [];
            const isExpanded = expandedTeamId === team.id;
            const memberUserIds = new Set(members.map((m) => String(m.userId)));
            const availableToAdd = workspaceMembers.filter(
              (m) => !memberUserIds.has(String(m.userId)),
            );

            return (
              <Card
                key={team.id}
                className="team-card stagger-item"
                style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}
              >
                <CardContent>
                  <Box className="team-card__header">
                    <Box
                      className="team-card__title-row"
                      onClick={() => handleToggleExpand(team.id)}
                    >
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      <Typography variant="h6">{team.name}</Typography>
                      <Chip size="small" label={`${members.length} member${members.length === 1 ? '' : 's'}`} />
                    </Box>
                    <Box className="team-card__actions">
                      <AvatarGroup max={4} sx={{ mr: 1 }}>
                        {members.map((m) => (
                          <Avatar key={m.userId} sx={{ width: 28, height: 28, fontSize: 13 }}>
                            {(m.user?.fullName || m.user?.userName || '?').charAt(0).toUpperCase()}
                          </Avatar>
                        ))}
                      </AvatarGroup>
                      {canManageMembers && (
                        <IconButton size="small" onClick={(e) => openMenu(e, team)} aria-label="Team options">
                          <MoreVertical size={18} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  <Collapse in={isExpanded}>
                    <Box className="team-card__members">
                      {members.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                          No members in this team yet.
                        </Typography>
                      )}
                      {members.map((m) => (
                        <Box key={m.userId} className="team-card__member-row">
                          <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                            {(m.user?.fullName || m.user?.userName || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">
                            {m.user?.fullName || m.user?.userName || `User ${m.userId}`}
                          </Typography>
                          <Chip size="small" variant="outlined" label={m.role} sx={{ ml: 1 }} />
                          {canManageMembers && (
                            <IconButton
                              size="small"
                              sx={{ ml: 'auto' }}
                              aria-label="Remove from team"
                              onClick={() => handleRemoveMember(team.id, m.userId)}
                            >
                              <X size={16} />
                            </IconButton>
                          )}
                        </Box>
                      ))}

                      {canManageMembers && (
                        <Box className="team-card__add-member">
                          <FormControl size="small" fullWidth>
                            <InputLabel shrink>Add member</InputLabel>
                            <Select
                              label="Add member"
                              value={addMemberTeamId === team.id ? selectedUserId : ''}
                              onChange={(e) => {
                                setAddMemberTeamId(team.id);
                                setSelectedUserId(e.target.value as string);
                              }}
                              displayEmpty
                            >
                              {availableToAdd.length === 0 && (
                                <MenuItem value="" disabled>
                                  All workspace members are already on this team
                                </MenuItem>
                              )}
                              {availableToAdd.map((m) => (
                                <MenuItem key={m.userId} value={m.userId}>
                                  {m.user?.name || m.user?.email || `User ${m.userId}`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <IconButton
                            color="primary"
                            disabled={addMemberTeamId !== team.id || !selectedUserId}
                            onClick={handleAddMember}
                            aria-label="Confirm add member"
                          >
                            <UserPlus size={18} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>

                    {/* Projects this team is assigned to - assigning here
                        and from a board's own "Manage Access" dialog write
                        to the same join table, so either entry point works. */}
                    <Box className="team-card__projects" sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Assigned Projects
                      </Typography>

                      {loadingBoardsForTeam === team.id ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={20} />
                        </Box>
                      ) : (
                        <>
                          {(teamBoards[team.id] || []).length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                              Not assigned to any project yet.
                            </Typography>
                          )}
                          {(teamBoards[team.id] || []).map((board) => (
                            <Box
                              key={board.id}
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
                            >
                              <Folder size={16} />
                              <Typography variant="body2">
                                {board.key ? `${board.key} · ${board.name}` : board.name}
                              </Typography>
                              {canManageMembers && (
                                <IconButton
                                  size="small"
                                  sx={{ ml: 'auto' }}
                                  aria-label="Unassign project"
                                  onClick={() => handleUnassignBoard(team.id, board.id)}
                                >
                                  <X size={16} />
                                </IconButton>
                              )}
                            </Box>
                          ))}

                          {canManageMembers && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                              <FormControl size="small" fullWidth>
                                <InputLabel shrink>Assign to project</InputLabel>
                                <Select
                                  label="Assign to project"
                                  value={assignBoardTeamId === team.id ? selectedBoardId : ''}
                                  onChange={(e) => {
                                    setAssignBoardTeamId(team.id);
                                    setSelectedBoardId(e.target.value as string);
                                  }}
                                  displayEmpty
                                >
                                  {(() => {
                                    const assignedIds = new Set(
                                      (teamBoards[team.id] || []).map((b) => String(b.id)),
                                    );
                                    const available = workspaceBoards.filter(
                                      (b) => !assignedIds.has(String(b.id)),
                                    );
                                    if (available.length === 0) {
                                      return (
                                        <MenuItem value="" disabled>
                                          {workspaceBoards.length === 0
                                            ? 'No projects in this workspace yet'
                                            : 'Already assigned to every project'}
                                        </MenuItem>
                                      );
                                    }
                                    return available.map((b) => (
                                      <MenuItem key={b.id} value={b.id}>
                                        {b.key ? `${b.key} · ${b.name}` : b.name}
                                      </MenuItem>
                                    ));
                                  })()}
                                </Select>
                              </FormControl>
                              <IconButton
                                color="primary"
                                disabled={
                                  assignBoardTeamId !== team.id || !selectedBoardId || assigningBoard
                                }
                                onClick={() => handleAssignBoard(team.id)}
                                aria-label="Confirm assign project"
                              >
                                <FolderPlus size={18} />
                              </IconButton>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Create team */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Team</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Team name"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !newTeamName.trim()}>
            {creating ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate name - expected outcome, friendly dialog not a toast */}
      <Dialog
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 4, textAlign: 'center', p: 1 } }}
      >
        <DialogContent sx={{ pt: 4, pb: 2 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            That name's taken
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A team called <strong>{newTeamName}</strong> already exists in this workspace. Pick a
            different name.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="contained" onClick={() => setDuplicateOpen(false)} sx={{ px: 4, borderRadius: 2 }}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename team */}
      <Dialog open={!!renamingTeam} onClose={() => setRenamingTeam(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename Team</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Team name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenamingTeam(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleRename} disabled={!renameValue.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete team confirmation */}
      <Dialog open={!!deletingTeam} onClose={() => setDeletingTeam(null)}>
        <DialogTitle>Delete team?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deletingTeam?.name}" will be permanently deleted. This does not remove its members
            from the workspace, and does not affect any projects.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingTeam(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team overflow menu (rename/copy/delete) */}
      {menuAnchorEl && menuTeam && (
        <TeamOverflowMenu
          anchorEl={menuAnchorEl}
          onClose={closeMenu}
          onRename={() => {
            setRenamingTeam(menuTeam);
            setRenameValue(menuTeam.name);
            closeMenu();
          }}
          onCopy={() => {
            setCopyingTeam(menuTeam);
            setCopyTargetWorkspaceId('');
            closeMenu();
          }}
          onDelete={() => {
            setDeletingTeam(menuTeam);
            closeMenu();
          }}
        />
      )}

      {/* Copy team to another workspace */}
      <Dialog open={!!copyingTeam} onClose={() => setCopyingTeam(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Copy Team to Another Workspace</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Duplicates "{copyingTeam?.name}" and its current members into the workspace you pick
            below. This workspace's copy is untouched. Members who aren't already part of the
            target workspace will be added there automatically.
          </DialogContentText>
          <FormControl fullWidth size="small">
            <InputLabel>Target workspace</InputLabel>
            <Select
              label="Target workspace"
              value={copyTargetWorkspaceId}
              onChange={(e) => setCopyTargetWorkspaceId(e.target.value as string)}
            >
              {workspaces
                .filter((w) => w.id !== workspaceId)
                .map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              {workspaces.filter((w) => w.id !== workspaceId).length === 0 && (
                <MenuItem value="" disabled>
                  No other workspaces available
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyingTeam(null)} disabled={copying}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCopyToWorkspace}
            disabled={copying || !copyTargetWorkspaceId}
          >
            {copying ? <CircularProgress size={20} /> : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const TeamOverflowMenu: React.FC<{
  anchorEl: HTMLElement;
  onClose: () => void;
  onRename: () => void;
  onCopy: () => void;
  onDelete: () => void;
}> = ({ anchorEl, onClose, onRename, onCopy, onDelete }) => (
  <Menu anchorEl={anchorEl} open onClose={onClose}>
    <MenuItem onClick={onRename}>
      <ListItemIcon>
        <Edit2 size={16} />
      </ListItemIcon>
      <ListItemText>Rename</ListItemText>
    </MenuItem>
    <MenuItem onClick={onCopy}>
      <ListItemIcon>
        <Copy size={16} />
      </ListItemIcon>
      <ListItemText>Copy to workspace...</ListItemText>
    </MenuItem>
    <MenuItem onClick={onDelete} sx={{ color: 'error.main' }}>
      <ListItemIcon>
        <Trash2 size={16} color="currentColor" />
      </ListItemIcon>
      <ListItemText>Delete team</ListItemText>
    </MenuItem>
  </Menu>
);

export default TeamsPage;
